"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { Phone } from "lucide-react";
import { T } from "gt-next";
import { FOOTER_LINKS } from "./constants/navigation-landing";
import LanguageDropdown from "@/components/landing/LanguageDropdown";
import FooterLanguageLinks from "@/components/landing/FooterLanguageLinks";
import AskAIAboutOutfame from "./footer/AskAIAboutOutfame";
import FollowerStats from "./footer/FollowerStats";
import { BRAND } from "@/lib/branding";

/* ──────────────────────────────────────────────
 * Static <T> label maps
 * gt-next CLI can only extract STATIC children.
 * We map each link's key → a <T> node so the
 * extractor sees every string at build time.
 * ──────────────────────────────────────────── */

const COMPANY_LABELS: Record<string, React.ReactNode> = {
  blog: <T>Blog</T>,
  affiliate: <T>Affiliate Program</T>,
  newsletter: <T>ROI Newsletter</T>,
  switch: <T>Switch to Outfame</T>,
  about: <T>About Us</T>,
  contact: <T>Contact Us</T>,
  start: <T>Get Started</T>,
  help: <T>Help Center</T>,
  careers: <T>Careers</T>,
  data: <T>Our Data</T>
};
const COMPANY_MOBILE_LABELS: Record<string, React.ReactNode> = {
  affiliate: <T>Affiliate</T>,
  about: <T>About</T>,
  contact: <T>Contact</T>
};
const COMPARE_LABELS: Record<string, React.ReactNode> = {
  "path-social": <T>Path Social Alternative</T>,
  nitreo: <T>Nitreo Alternative</T>,
  kicksta: <T>Kicksta Alternative</T>,
  plixi: <T>Plixi Alternative</T>,
  ampfluence: <T>Ampfluence Alternative</T>,
  upleap: <T>Upleap Alternative</T>,
  "view-all-compare": <T>View all comparisons</T>
};
const FEATURE_LABELS: Record<string, React.ReactNode> = {
  "how-it-works": <T>How It Works</T>,
  targeting: <T>Instagram Targeting</T>,
  "safe-growth": <T>Safe Instagram Growth</T>,
  "real-followers": <T>Real Instagram Followers</T>,
  analytics: <T>Instagram Analytics</T>,
  automation: <T>Instagram Automation</T>
};
const TOOL_LABELS: Record<string, React.ReactNode> = {
  "fake-check": <T>Fake Follower Check</T>,
  "engagement-calc": <T>Engagement Calculator</T>,
  "influencer-search": <T>Influencer Search</T>,
  "profile-analyzer": <T>Profile Analyzer</T>,
  "hashtag-generator": <T>Hashtag Generator</T>,
  "all-tools": <T>All Free Instagram Tools</T>
};
const LEGAL_LABELS: Record<string, React.ReactNode> = {
  terms: <T>Terms & Service</T>,
  privacy: <T>Privacy Policy</T>
};
const BADGE_LABELS: Record<string, React.ReactNode> = {
  "Earn 50%": <T>Earn 50%</T>,
  Hiring: <T>Hiring</T>
};
const SERVICE_LABELS: Record<string, React.ReactNode> = {
  "buy-followers": <T>Buy Instagram Followers</T>,
  "growth-service": <T>Instagram Growth Service</T>,
  "free-followers": <T>Free Instagram Followers</T>,
  "real-followers": <T>Real Instagram Followers</T>,
  "instagram-automation": <T>Instagram Automation</T>,
  "buy-likes": <T>Buy Instagram Likes</T>,
  "buy-views": <T>Buy Instagram Views</T>
};
const RESOURCE_LABELS: Record<string, React.ReactNode> = {
  "blog-resource": <T>Blog</T>,
  "how-it-works-resource": <T>How It Works</T>,
  reviews: <T>Outfame Reviews</T>,
  "influencer-guide": <T>Influencer Marketing Guide</T>,
  results: <T>Growth Results</T>,
  "pricing-resource": <T>Pricing</T>
};
const LOCATION_CITIES: Record<string, React.ReactNode> = {
  la: <T>Los Angeles, US</T>,
  sg: <T>Singapore, SG</T>
};
const LOCATION_ADDRESSES: Record<string, React.ReactNode> = {
  la: <T>{"2049 Century Park E\nLos Angeles, CA 90067"}</T>,
  sg: <T>{"1 Raffles Place, Tower 1\nSingapore 048616"}</T>
};

// Social icons externalized to SVG files for HTML size reduction
const SOCIAL_ICON_PATHS: Record<string, string> = {
  instagram: "/img/icons/social/instagram-footer.svg",
  x: "/img/icons/social/x-footer.svg",
  youtube: "/img/icons/social/youtube-footer.svg",
  tiktok: "/img/icons/social/tiktok-footer.svg",
  linkedin: "/img/icons/social/linkedin-footer.svg"
};
export default function Footer() {
  const $ = _c(51);
  const [visible, setVisible] = useState(false);
  const [animReady, setAnimReady] = useState(false);
  const footerRef = useRef(null);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setAnimReady(true);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[2] !== animReady) {
    t2 = () => {
      if (!animReady) {
        return;
      }
      const el = footerRef.current;
      if (!el) {
        return;
      }
      const observer = new IntersectionObserver(t4 => {
        const [entry] = t4;
        setVisible(entry.isIntersecting);
      }, {
        threshold: 0.15,
        rootMargin: "0px 0px -12% 0px"
      });
      observer.observe(el);
      return () => observer.disconnect();
    };
    t3 = [animReady];
    $[2] = animReady;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  const show = !animReady || visible;
  const transitionBase = animReady ? "transition-[opacity,transform] duration-700 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]" : "";
  const t4 = `mb-10 grid grid-cols-2 gap-x-4 gap-y-10 lg:mb-16 lg:grid-cols-5 lg:gap-6 ${transitionBase}`;
  let t5;
  if ($[5] !== animReady || $[6] !== show) {
    t5 = animReady ? {
      opacity: show ? 1 : 0,
      transform: show ? "translateY(0)" : "translateY(24px)"
    } : undefined;
    $[5] = animReady;
    $[6] = show;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="mb-6"><img src="/img/outfame-logo-full-white.svg" alt="Outfame" width={140} height={28} className="h-7 w-auto" /></div>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="grid grid-cols-2 gap-6 lg:grid-cols-1">{FOOTER_LINKS.locations.map(_temp)}{BRAND.supportPhone && <div className="col-span-2 lg:col-span-1"><p className="mb-0.5 text-[11px] font-semibold uppercase tracking-wide text-[rgb(255,163,209)]">AI Support Line</p><a href={`tel:${BRAND.supportPhone.replace(/[^+\d]/g, "")}`} aria-label={`Call support at ${BRAND.supportPhone}`} className="flex min-h-[44px] items-center gap-2 transition-opacity hover:opacity-80"><Phone className="h-4 w-4 shrink-0 text-[rgb(255,163,209)]" /><span className="rounded-[8px] bg-[rgb(255,163,209)] px-2.5 py-1 text-[14px] font-semibold leading-[130%] text-[rgb(20,20,20)]">{BRAND.supportPhone}</span></a></div>}</div>;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <LanguageDropdown variant="dark" />;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="lg:order-2">{t8}<div className="mt-2"><FooterLanguageLinks /></div></div>;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="-ml-3 flex gap-0 lg:order-1">{FOOTER_LINKS.social.map(_temp2)}</div>;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="lg:order-0 order-first col-span-2 lg:col-span-1">{t6}{t7}<div className="mt-6 grid grid-cols-2 gap-6 lg:flex lg:flex-col lg:gap-3">{t9}{t10}<div className="col-span-2 mt-4 hidden lg:order-3 lg:mt-4 lg:block"><AskAIAboutOutfame /></div></div></div>;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <h4 className="mb-3 text-[14px] font-semibold leading-[24px] text-white lg:text-[16px]"><T>Popular Services</T></h4>;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div>{t12}<ul className="space-y-1.5">{FOOTER_LINKS.services.map(_temp3)}</ul></div>;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <h4 className="mb-3 text-[14px] font-semibold leading-[24px] text-white lg:text-[16px]"><T>Resources</T></h4>;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="contents lg:block">{t13}<div className="lg:mt-8">{t14}<ul className="space-y-1.5">{FOOTER_LINKS.resources.map(_temp4)}</ul></div></div>;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <h4 className="mb-3 text-[14px] font-semibold leading-[24px] text-white lg:text-[16px]"><T>Features</T></h4>;
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  let t17;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div>{t16}<ul className="space-y-1.5">{FOOTER_LINKS.features.map(_temp5)}</ul></div>;
    $[19] = t17;
  } else {
    t17 = $[19];
  }
  let t18;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <h4 className="mb-3 text-[14px] font-semibold leading-[24px] text-white lg:text-[16px]"><T>Free Tools</T></h4>;
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  let t19;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="contents lg:block">{t17}<div className="order-last lg:order-none lg:mt-8">{t18}<ul className="space-y-1.5">{FOOTER_LINKS.tools.map(_temp6)}</ul></div></div>;
    $[21] = t19;
  } else {
    t19 = $[21];
  }
  let t20;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <h4 className="mb-3 text-[14px] font-semibold leading-[24px] text-white lg:text-[16px]"><T>Compare</T></h4>;
    $[22] = t20;
  } else {
    t20 = $[22];
  }
  let t21;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div>{t20}<ul className="space-y-1.5">{FOOTER_LINKS.compare.map(_temp7)}</ul></div>;
    $[23] = t21;
  } else {
    t21 = $[23];
  }
  let t22;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <h4 className="mb-3 text-[14px] font-semibold leading-[24px] text-white lg:text-[16px]"><T>Company</T></h4>;
    $[24] = t22;
  } else {
    t22 = $[24];
  }
  let t23;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="contents lg:block">{t21}<div className="lg:mt-8">{t22}<ul className="space-y-1.5">{FOOTER_LINKS.company.map(_temp8)}</ul></div></div>;
    $[25] = t23;
  } else {
    t23 = $[25];
  }
  let t24;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <div className="hidden lg:block"><FollowerStats /></div>;
    $[26] = t24;
  } else {
    t24 = $[26];
  }
  let t25;
  if ($[27] !== t4 || $[28] !== t5) {
    t25 = <div className={t4} style={t5}>{t11}{t15}{t19}{t23}{t24}</div>;
    $[27] = t4;
    $[28] = t5;
    $[29] = t25;
  } else {
    t25 = $[29];
  }
  const t26 = `mb-10 flex flex-col items-center gap-8 border-b border-white/10 pb-10 lg:hidden ${transitionBase}`;
  let t27;
  if ($[30] !== animReady || $[31] !== show) {
    t27 = animReady ? {
      opacity: show ? 1 : 0,
      transform: show ? "translateY(0)" : "translateY(20px)",
      transitionDelay: show ? "150ms" : "0ms"
    } : undefined;
    $[30] = animReady;
    $[31] = show;
    $[32] = t27;
  } else {
    t27 = $[32];
  }
  let t28;
  let t29;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <AskAIAboutOutfame />;
    t29 = <FollowerStats />;
    $[33] = t28;
    $[34] = t29;
  } else {
    t28 = $[33];
    t29 = $[34];
  }
  let t30;
  if ($[35] !== t26 || $[36] !== t27) {
    t30 = <div className={t26} style={t27}>{t28}{t29}</div>;
    $[35] = t26;
    $[36] = t27;
    $[37] = t30;
  } else {
    t30 = $[37];
  }
  const t31 = `flex flex-col gap-4 ${transitionBase}`;
  let t32;
  if ($[38] !== animReady || $[39] !== show) {
    t32 = animReady ? {
      opacity: show ? 1 : 0,
      transform: show ? "translateY(0)" : "translateY(16px)",
      transitionDelay: show ? "300ms" : "0ms"
    } : undefined;
    $[38] = animReady;
    $[39] = show;
    $[40] = t32;
  } else {
    t32 = $[40];
  }
  let t33;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <p className="text-[12px] font-normal text-white/60 lg:text-[14px]"><T>© 2026 Outfame. All rights reserved.</T></p>;
    $[41] = t33;
  } else {
    t33 = $[41];
  }
  let t34;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = FOOTER_LINKS.legal.map(_temp9);
    $[42] = t34;
  } else {
    t34 = $[42];
  }
  let t35;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <div className="flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">{t33}<div className="flex flex-wrap gap-1 lg:gap-2">{t34}<Link href="/llms.txt" className="inline-flex min-h-[44px] items-center px-2 text-[12px] font-normal text-white/60 transition-colors hover:text-white lg:text-[14px]"><T>AI information</T></Link></div></div>;
    $[43] = t35;
  } else {
    t35 = $[43];
  }
  let t36;
  if ($[44] !== t31 || $[45] !== t32) {
    t36 = <div className={t31} style={t32}>{t35}</div>;
    $[44] = t31;
    $[45] = t32;
    $[46] = t36;
  } else {
    t36 = $[46];
  }
  let t37;
  if ($[47] !== t25 || $[48] !== t30 || $[49] !== t36) {
    t37 = <footer ref={footerRef} className="px-3 pb-3"><div className="w-full"><div className="rounded-[20px] bg-[rgb(20,20,20)] px-5 py-12 md:px-10 md:py-20 lg:px-16 lg:pb-4 lg:pt-40">{t25}{t30}{t36}</div></div></footer>;
    $[47] = t25;
    $[48] = t30;
    $[49] = t36;
    $[50] = t37;
  } else {
    t37 = $[50];
  }
  return t37;
}
function _temp9(link_5, index_7) {
  return <Link key={index_7} href={link_5.href} className="inline-flex min-h-[44px] items-center px-2 text-[12px] font-normal text-white/60 transition-colors hover:text-white lg:text-[14px]">{LEGAL_LABELS[link_5.key] || link_5.label}</Link>;
}
function _temp8(link_4, index_6) {
  return <li key={index_6}>{link_4.href.startsWith("http") ? <a href={link_4.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[14px] font-normal leading-[21px] text-white/60 transition-colors hover:text-white lg:text-[16px] lg:leading-[24px]">{COMPANY_LABELS[link_4.key] || link_4.label}{link_4.badge && <span className="rounded-[8px] bg-[rgb(255,163,209)] px-2 py-1 text-[12px] font-semibold leading-[130%] text-[rgb(20,20,20)]">{BADGE_LABELS[link_4.badge] || link_4.badge}</span>}</a> : <Link href={link_4.href} className="inline-flex items-center gap-2 text-[14px] font-normal leading-[21px] text-white/60 transition-colors hover:text-white lg:text-[16px] lg:leading-[24px]">{link_4.mobileLabel ? <><span className="lg:hidden">{COMPANY_MOBILE_LABELS[link_4.key] || link_4.mobileLabel}</span><span className="hidden lg:inline">{COMPANY_LABELS[link_4.key] || link_4.label}</span></> : COMPANY_LABELS[link_4.key] || link_4.label}{link_4.badge && <span className="rounded-[8px] bg-[rgb(255,163,209)] px-2 py-1 text-[12px] font-semibold leading-[130%] text-[rgb(20,20,20)]">{BADGE_LABELS[link_4.badge] || link_4.badge}</span>}</Link>}</li>;
}
function _temp7(link_3, index_5) {
  return <li key={index_5}><Link href={link_3.href} className={`text-[14px] font-normal leading-[21px] transition-colors hover:text-white lg:text-[16px] lg:leading-[24px] ${link_3.isViewAll ? "text-white/80 underline underline-offset-2" : "text-white/60"}`}>{COMPARE_LABELS[link_3.key] || link_3.label}</Link></li>;
}
function _temp6(link_2, index_4) {
  return <li key={index_4}><Link href={link_2.href} className={`text-[14px] font-normal leading-[21px] transition-colors hover:text-white lg:text-[16px] lg:leading-[24px] ${link_2.isViewAll ? "text-white/80 underline underline-offset-2" : "text-white/60"}`}>{TOOL_LABELS[link_2.key] || link_2.label}</Link></li>;
}
function _temp5(link_1, index_3) {
  return <li key={index_3}><Link href={link_1.href} className="text-[14px] font-normal leading-[21px] text-white/60 transition-colors hover:text-white lg:text-[16px] lg:leading-[24px]">{FEATURE_LABELS[link_1.key] || link_1.label}</Link></li>;
}
function _temp4(link_0, index_2) {
  return <li key={index_2}><Link href={link_0.href} className="text-[14px] font-normal leading-[21px] text-white/60 transition-colors hover:text-white lg:text-[16px] lg:leading-[24px]">{RESOURCE_LABELS[link_0.key] || link_0.label}</Link></li>;
}
function _temp3(link, index_1) {
  return <li key={index_1}><Link href={link.href} className="text-[14px] font-normal leading-[21px] text-white/60 transition-colors hover:text-white lg:text-[16px] lg:leading-[24px]">{SERVICE_LABELS[link.key] || link.label}</Link></li>;
}
function _temp2(social, index_0) {
  return <Link key={index_0} href={social.href} className="flex h-11 w-11 items-center justify-center rounded-lg text-white transition-opacity hover:opacity-70" aria-label={social.label} target="_blank" rel="noopener noreferrer"><img src={SOCIAL_ICON_PATHS[social.icon]} alt="" aria-hidden="true" width={22} height={22} loading="lazy" decoding="async" /></Link>;
}
function _temp(location, index) {
  return <div key={index}><h4 className="mb-3 text-[14px] font-semibold leading-[24px] text-white lg:text-[16px]">{LOCATION_CITIES[location.key] || location.city}</h4><p className="whitespace-pre-line text-[14px] font-normal leading-[21px] text-white/60 lg:text-[16px] lg:leading-[24px]">{LOCATION_ADDRESSES[location.key] || location.address}</p></div>;
}